import { Choice, ContentType, ContextVariableToSet, DiagnosticsActionStatus, ExternalApi, GraphNode, JsonDocument, ParamOrVar, RaisedEventStatus, SolutionContent } from '../../models';
import { Error as ErrorModel } from '../../models/app.models';
import { DiagnosticsInitResponse, DiagnosticsProgressResponse, ErrorCodes, FormFieldV2 } from '../../models/safDiagnostic.models';
import { createActionTypes, createImmutableMap, IImmutableList, IImmutableMap, IImmutableWeakTypedMap, MDispatch } from '../../state/redux';
import { IRuntimeStaticConfig, IsUserSignedInHandlerType, LogHandlerType } from '../../types';

export const IntentGraphAppActionTypes = createActionTypes(
  {
    InitializeApp: 'InitializeApp',
    UpdateGlobalVars: 'UpdateGlobalVars',
    ResetStore: 'ResetStore'
  },
  __filename
);

export const initializeAppAction = (staticConfig: IRuntimeStaticConfig, logHandler?: LogHandlerType, isUserSignedIn?: IsUserSignedInHandlerType) => {
  return {
    type: IntentGraphAppActionTypes.InitializeApp,
    payload: { staticConfig: staticConfig, logHandler: logHandler, isUserSignedIn: isUserSignedIn }
  };
};

export const resetStoreAction = () => {
  return {
    type: IntentGraphAppActionTypes.ResetStore
  };
};

export const setContextVarsAction = (adhocVariables: IImmutableList<IImmutableMap<ContextVariableToSet>>, nodeIndex: number) => {
  return {
    type: NodeActionTypes.SetContextVars,
    payload: {
      adhocVariables,
      nodeIndex
    }
  };
};

export const fetchDialogStartedAction = () => {
  return {
    type:NodeActionTypes.FetchingDialogStarted,
  };
};

export const fetchDialogCompleteAction = (jsonDocument:IImmutableMap<JsonDocument>, parentJsonDocument:IImmutableMap<JsonDocument>, sessionId: string, logHandler?: LogHandlerType, isUserSignedIn?: IsUserSignedInHandlerType, dialogFound?: boolean, failedTargetNode?: IImmutableMap<GraphNode>) => {
  return {
    type:NodeActionTypes.FetchingDialogComplete,
    payload:{ 
      jsonDocument: jsonDocument,
      parentJsonDocument: parentJsonDocument,
      sessionId: sessionId, 
      logHandler: logHandler, 
      isUserSignedIn: isUserSignedIn, 
      dialogFound: dialogFound,
      failedTargetNode 
    }
  };
};

export const NodeActionTypes = createActionTypes(
  {
    PushTargetNodeOnRenderedNodes: 'PushTargetNodeOnRenderedNodes',
    EditNodeChoiceSelection: 'EditNodeChoiceSelection',
    CancelEditNodeChoiceSelection: 'CancelEditNodeChoiceSelection',
    DiagnosticsActionStarted: 'DiagnosticsActionStarted',
    DiagnosticsActionCompleted: 'DiagnosticsActionCompleted',
    StartDiagnosticsRun: 'StartDiagnosticsRun',
    ResetDiagnosticsRunStatus: 'ResetDiagnosticsRunStatus',
    SetDiagnosticsRunProgress: 'SetDiagnosticsRunProgress',
    SetDiagnosticsStatus: 'SetDiagnosticsStatus',
    IncrementDiagnosticsRunProgress: 'IncrementDiagnosticsRunProgress',
    UpdateDiagnosticsFormComponent: 'UpdateDiagnosticsFormComponent',
    AbandonDiagnosticsRun: 'AbandonDiagnosticsRun',
    LoadDiagnosticResource: 'LoadDiagnosticResource',
    ResetSafDiagnostic: 'ResetSafDiagnostic',
    StopDiagnosticsTimer: 'StopDiagnosticsTimer',
    StartDiagnosticsTimer: 'StartDiagnosticsTimer',
    FetchingSafDiagnostics: 'FetchingSafDiagnostics',
    SafDiagnosticsCompleted: 'SafDiagnosticsCompleted',
    SafDiagnosticsRendered: 'SafDiagnosticsRendered',
    UpdateRaisedEvent: 'UpdateRaisedEvent',
    UpdateApiRetryCount: 'UpdateApiRetryCount',
    ResetApiRetryCount: 'ResetApiRetryCount',
    UpdateInputText: 'UpdateInputText',
    PushTargetNodeFromInputNode: 'PushTargetNodeFromInputNode',
    UpdateInputFormFields: 'UpdateInputFormFields',
    FetchingDialogComplete: 'FetchingDialogComplete',
    FetchingDialogStarted: 'FetchingDialogStarted',
    SetContextVars: 'SetContextVars',
    SetAlchemyResource: 'SetAlchemyResource',
    FetchAlchemyResource: 'FetchAlchemyResource'
  },
  __filename
);

export const errorManagerActionTypes = createActionTypes(
  {
    SetError: 'SetError',
    ResetError: 'ResetError',
    ResetErrors: 'ResetErrors'
  },
  __filename
);

export const pushTargetNodeOnRenderedNodesAction = (
  uId: string,
  targetNode: IImmutableMap<GraphNode>,
  selectedChoice: IImmutableMap<Choice>,
  isDynamicChoice: boolean,
  choiceIndex: number, 
  preConditionNodes?: IImmutableMap<GraphNode>[]
) => {
  return {
    type: NodeActionTypes.PushTargetNodeOnRenderedNodes,
    payload: {
      uId: uId,
      targetNode: targetNode,
      selectedChoice: selectedChoice,
      isDynamicChoice: isDynamicChoice,
      choiceIndex: choiceIndex,
      preConditionNodes: preConditionNodes
    }
  };
};

export const pushTargetNodeFromInputNodeAction = (
  uId: string,
  targetNode: IImmutableMap<GraphNode>,
  dialogId: string,
  preConditionNodes?: IImmutableMap<GraphNode>[],
) => {
  return {
    type: NodeActionTypes.PushTargetNodeFromInputNode,
    payload: {
      uId: uId,
      targetNode: targetNode,
      preConditionNodes: preConditionNodes,
      dialogId: dialogId
    }
  };
};

export const updateInputFormFieldsAction = (inputFormFields: IImmutableMap<FormFieldV2>, uId: string)=>{
  return {
    type: NodeActionTypes.UpdateInputFormFields,
    payload: {
      inputFormFields,
      uId
    }
  };
}

export const safDiagnosticRenderedAction = (
  uId: string,
  targetNode: IImmutableMap<GraphNode>,
  nodeId: string,
  diagnosticUId: string,
  success?: boolean,
  preConditionNodes?: IImmutableMap<GraphNode>[]
) => {
  return {
    type: NodeActionTypes.SafDiagnosticsRendered,
    payload: {
      uId: uId,
      targetNode: targetNode,
      nodeId: nodeId,
      success: !!success,
      diagnosticUId: diagnosticUId,
      preConditionNodes: preConditionNodes
    }
  };
};

export const editNodeChoiceSelectionAction = (
  uId: string,
  prevChoiceSelections: IImmutableList<IImmutableMap<GraphNode>>,
  nodeIndex: number
) => {
  return {
    type: NodeActionTypes.EditNodeChoiceSelection,
    payload: { prevChoiceSelections: prevChoiceSelections, uId: uId, nodeIndex: nodeIndex}
  };
};

export const cancelEditNodeChoiceSelectionAction = (prevChoiceSelections: IImmutableList<IImmutableMap<GraphNode>>) => {
  return {
    type: NodeActionTypes.CancelEditNodeChoiceSelection,
    payload: prevChoiceSelections
  };
};

export const diagnosticsStartedAction = (uId: string, timerId: number) => {
  return {
    type: NodeActionTypes.DiagnosticsActionStarted,
    payload: { timerId: timerId, uId: uId }
  };
};

export const safDiagnosticsCompletedAction = () => {
  return {
    type: NodeActionTypes.SafDiagnosticsCompleted
  };
};

/** 
 * for diagnostics not using uuId because hosts will need to make changes to 
 * pass back uId after completion,
 *  instead for now we'll update the last diagnostic
 * */ 
export const diagnosticsCompletedAction = (
  nodeId: string,
  diagnosticsActionStatus: DiagnosticsActionStatus,
  targetNode: IImmutableMap<GraphNode>,
  isError: boolean = false,
  uId: string,
  isInEditMode?: boolean,
  preConditionNodes?: IImmutableMap<GraphNode>[]
) => {
  return {
    type: NodeActionTypes.DiagnosticsActionCompleted,
    payload: {
      nodeId: nodeId,
      diagnosticsActionStatus: diagnosticsActionStatus,
      targetNode: targetNode,
      isError: isError,
      isInEditMode: isInEditMode,
      uId: uId,
      preConditionNodes: preConditionNodes
    }
  };
};

export const updateGlobalVarsAction = (newVars: IImmutableList<IImmutableMap<ParamOrVar>>) => {
  return {
    type: IntentGraphAppActionTypes.UpdateGlobalVars,
    payload: newVars
  };
};

export const setDiagnosticsRunProgressAction = (response: IImmutableMap<DiagnosticsProgressResponse>) => {
  return {
    type: NodeActionTypes.SetDiagnosticsRunProgress,
    payload: response
  };
};

export const loadDiagnosticResourceAction = (initResponse?: IImmutableMap<DiagnosticsInitResponse>, jsonSchemaForm?: IImmutableWeakTypedMap) => {
  return {
    type: NodeActionTypes.LoadDiagnosticResource,
    payload: { diagnostic: initResponse, jsonSchemaForm: jsonSchemaForm }
  };
};

export const updateDiagnosticsFormComponentAction = (
  componentKey: string,
  updatedModel: string | number | boolean | Date | null | undefined
) => {
  return {
    type: NodeActionTypes.UpdateDiagnosticsFormComponent,
    payload: {
      componentKey: componentKey,
      updatedModel: updatedModel
    }
  };
};

export const resetSafDiagnosticAction = () => {
  return {
    type: NodeActionTypes.ResetSafDiagnostic
  };
};

export const startDiagnosticsRunAction = (response: IImmutableMap<DiagnosticsProgressResponse>) => {
  return {
    type: NodeActionTypes.StartDiagnosticsRun,
    payload: response
  };
};

export const setDiagnosticsStatusAction = (status: string, isTimeout?: boolean) => {
  return {
    type: NodeActionTypes.SetDiagnosticsStatus,
    payload: { status: status, isTimeout: isTimeout }
  };
};

export const abandonDiagnosticsRunAction = (abandon: boolean) => {
  return {
    type: NodeActionTypes.AbandonDiagnosticsRun,
    payload: abandon
  };
};

export const resetDiagnosticsRunStatusAction = () => {
  return {
    type: NodeActionTypes.ResetDiagnosticsRunStatus
  };
};

export const incrementDiagnosticsRunProgressAction = () => {
  return {
    type: NodeActionTypes.IncrementDiagnosticsRunProgress
  };
};

export const startDiagnosticsTimerAction = (timerId: number) => {
  return {
    type: NodeActionTypes.StartDiagnosticsTimer,
    payload: timerId
  };
};

export const stopDiagnosticsTimerAction = () => {
  return {
    type: NodeActionTypes.StopDiagnosticsTimer
  };
};

export const fetchingSafDiagnosticsAction = (completed: boolean, uId: string) => {
  return {
    type: NodeActionTypes.FetchingSafDiagnostics,
    payload: { completed, uId }
  };
};

export const setDiagnosticsRunProgress = (response: IImmutableMap<DiagnosticsProgressResponse>) => {
  return {
    type: NodeActionTypes.SetDiagnosticsRunProgress,
    payload: response
  };
};

export const resetErrorAction = (componentKey: string) => {
  return {
    type: errorManagerActionTypes.ResetError,
    payload: {
      componentKey: componentKey
    }
  };
};

export const resetErrorsAction = (componentKey: string, errorCodes: string[]) => {
  return {
    type: errorManagerActionTypes.ResetErrors,
    payload: {
      componentKey: componentKey,
      errorCodes: errorCodes
    }
  }
}

export const dispatchResetErrorAction = (componentKey: string) => {
  return async (dispatch: MDispatch) => {
    dispatch(resetErrorAction(componentKey));
  };
};

export const setErrorAction = (componentKey: string, errorCode: ErrorCodes) => {
  const error: ErrorModel = {
    ComponentKey: componentKey,
    ErrorCode: errorCode
  };
  return {
    type: errorManagerActionTypes.SetError,
    payload: {
      componentKey: componentKey,
      error: createImmutableMap(error)
    }
  };
};

export const dispatchSetErrorAction = (componentKey: string, errorCode: ErrorCodes) => {
  return async (dispatch: MDispatch) => {
    if (errorCode) {
      dispatch(setErrorAction(componentKey, errorCode));
    }
  };
};

export const updateRaisedEventAction = (uId: string, eventName: string, status: RaisedEventStatus) => {
  return {
    type: NodeActionTypes.UpdateRaisedEvent,
    payload: { uId, eventName, status }
  };
};

export const updateApiRetryCountAction = (name: ExternalApi, retryCount: number) => {
  return {
    type: NodeActionTypes.UpdateApiRetryCount,
    payload: { name, retryCount }
  };
};

export const resetApiRetryCountAction = (name: ExternalApi) => {
  return {
    type: NodeActionTypes.ResetApiRetryCount,
    payload: { name }
  };
};

export const updateInputTextAction = (
  text: string,
  uId: string
) => {
  return {
    type: NodeActionTypes.UpdateInputText,
    payload: {
      uId: uId,
      text: text
    }
  };
};

export const setAlchemyResourceAction = (response: SolutionContent, contentId: string) => {
  return {
    type: NodeActionTypes.SetAlchemyResource,
    payload: { response, contentId }
  };
};

export const fetchAlchemyResourceAction = (contentId: string, isDynamic: boolean, contentType: ContentType) => {
  return {
    type: NodeActionTypes.FetchAlchemyResource,
    payload: { contentId, isDynamic, contentType }
  };
};